import type { Session } from "@supabase/supabase-js";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminRequest, type UserLinkageRow } from "../adminApi";
import { LoadingPanel } from "../components/LoadingPanel";
import { Pagination } from "../components/Pagination";
import { Status } from "../components/Status";

const PAGE_SIZE = 50;

export function UsersPage({
  mfaToken,
  session,
  setNotice
}: {
  mfaToken: string | null;
  session: Session;
  setNotice(n: { tone: "error" | "success"; text: string } | null): void;
}) {
  const [users, setUsers] = useState<UserLinkageRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const loadPage = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const offset = (p - 1) * PAGE_SIZE;
      const data = await adminRequest<{ users: UserLinkageRow[]; total_count: number }>(
        `/users?offset=${offset}&limit=${PAGE_SIZE}`,
        { mfaToken, session }
      );
      setUsers(data.users);
      setTotalCount(data.total_count);
      setPage(p);
    } catch (caught) {
      setNotice({ tone: "error", text: caught instanceof Error ? caught.message : "Could not load users." });
    } finally {
      setLoading(false);
    }
  }, [mfaToken, session, setNotice]);

  useEffect(() => { void loadPage(1); }, []);

  return (
    <div className="stack">
      <section className="section">
        <div className="sectionHead">
          <div>
            <h1>Users & linked rows</h1>
            <p>Profile records connected to invitations, vehicles, workflows, and run activity.</p>
          </div>
          <button onClick={() => void loadPage(page)} type="button">Refresh</button>
        </div>
        {loading ? <LoadingPanel label="Loading users…" /> : <UserTable users={users} />}
        <Pagination currentPage={page} pageSize={PAGE_SIZE} totalCount={totalCount} onPageChange={loadPage} />
      </section>
    </div>
  );
}

function UserTable({ users }: { users: UserLinkageRow[] }) {
  return (
    <div className="tableWrap">
      <table>
        <thead>
          <tr>
            <th>User</th>
            <th>Invitation</th>
            <th>Vehicles</th>
            <th>Workflows</th>
            <th>Runs</th>
            <th>Last run</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>
                <Link to={`/users/${user.id}`}>
                  <strong>{user.email}</strong>
                  <span>{user.id.slice(0, 8).toUpperCase()}</span>
                </Link>
              </td>
              <td>{user.invitation_status ? <Status value={user.invitation_status} /> : "—"}</td>
              <td>{user.linked_vehicles}</td>
              <td>{user.workflows}</td>
              <td>{user.total_runs}</td>
              <td>
                {user.recent_run_status ? <Status value={user.recent_run_status} /> : "—"}
                {user.last_run_at ? <span>{formatDate(user.last_run_at)}</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!users.length ? <div className="empty">No users found.</div> : null}
    </div>
  );
}

function formatDate(value: string) {
  return new Date(value).toLocaleString();
}
